import React, { useState, useMemo } from "react";
import { Modal, Typography, Button, Alert, message, Spin, Divider } from "antd";
import {
  CloseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { JobPostFormData } from "../../../schemas/jobPostValidation";
import { transformFormDataToPayload } from "../../../utils/formTransformers";
import {
  getCurrentUserAndOrg,
  generateStorageKey,
} from "../../../utils/storageUtils";
import {
  validateStep1Complete,
  validateStep2Complete,
  validateStep3Complete,
  validateStep5Complete,
} from "../../../hooks/useFormValidation";
import { useSubscriptionLimit } from "../../../hooks/useSubscriptionLimit";
import useOrganization from "../../../../../Hooks/useOrganization";
import jobDescriptionService from "../../../../../Service/job.description.service";
import { JobDescriptionEntity } from "../../../../../Entities/JobDescription";
import {
  getUserFriendlyErrorMessage,
  logError,
  withRetry,
} from "../../../utils/errorHandling";
import interviewService from "../../../../../Service/interview.service";
import simulationService from "../../../../../Service/simulation.service";
import AuthService from "../../../../../Service/auth.service";
import ReviewSummaryCard from "./ReviewSummaryCard";
import ReviewSummaryContents from "./ReviewSummaryContents";
import { CHART_COLORS } from "../../../../JobPostDetails/components/StatsAndAnalytics/utils/chartColors";
import useAccess from "../../../../../Hooks/useAccess";
import SubscriptionWarning from "../../../../../Components/SubscriptionWarning";
import "./ReviewModal.scss";

const { Title, Text } = Typography;

interface ReviewModalProps {
  visible: boolean;
  onClose: () => void;
  orgName: string;
  onStepChange?: (step: number) => void;
  /** Draft ID used to locate and remove the correct localStorage slot on publish. */
  draftId: string;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  visible,
  onClose,
  orgName,
  onStepChange,
  draftId,
}) => {
  const navigate = useNavigate();
  const { watch } = useFormContext<JobPostFormData>();
  const [isPublishing, setIsPublishing] = useState(false);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const { changeSubscriptionMeasurements } = useAccess();
  const { org } = useOrganization();
  const { checkLimit } = useSubscriptionLimit();

  // Watch form data to ensure updates are reflected
  // Explicitly watch step0 to ensure ATS info is included
  const formData = watch();
  const step0Data = watch("step0");
  const mode = (formData.mode || "new") as "new" | "edit" | "clone";
  const jobPostId = formData.jobPostId;

  // Ensure step0 is included in formData (merge if needed)
  const completeFormData = {
    ...formData,
    step0: step0Data || formData.step0 || { prompt: "" },
  };

  // Check if each step is complete
  const isStepComplete = (stepNumber: number): boolean => {
    switch (stepNumber) {
      case 1:
        return validateStep1Complete(completeFormData.step1);
      case 2:
        return validateStep2Complete(completeFormData.step2);
      case 3:
        return validateStep3Complete(completeFormData.step3);
      case 4:
        return true; // Job Alignment is optional
      case 5:
        return validateStep5Complete(completeFormData.step5);
      default:
        return false;
    }
  };

  const incompleteSteps = [1, 2, 3, 5].filter((step) => !isStepComplete(step));
  const allStepsComplete = incompleteSteps.length === 0;

  const handleEditStep = (stepNumber: number) => {
    onClose(); // Close review modal
    if (onStepChange) {
      onStepChange(stepNumber);
    }
  };

  const handlePublishClick = async () => {
    if (!allStepsComplete) {
      message.warning("Please complete all required steps before publishing");
      return;
    }

    // Final limit check right before API call (defense in depth)
    const limitCheck = checkLimit(mode);

    if (limitCheck.shouldBlock && mode !== "edit") {
      setShowUpgradePopup(true);
      if (limitCheck.reason) {
        message.error(limitCheck.reason, 4);
      }
      return;
    }

    // Use getGenericRoundsOnly() to only fetch generic rounds (profile-match, overall-assessment, job-alignment)
    // This avoids processing custom rounds that aren't needed for ID mapping
    const allRoundsInDB = await interviewService.getGenericRoundsOnly();

    setIsPublishing(true);
    try {
      // Log formData step0 to verify ATS info is present
      console.log(
        "[ReviewModal] FormData step0 before transform:",
        completeFormData.step0,
      );
      console.log("[ReviewModal] Full formData:", completeFormData);

      const jobPostData = transformFormDataToPayload(
        completeFormData,
        allRoundsInDB,
      );
      console.log(
        "[ReviewModal] Transformed payload:",
        JSON.stringify(jobPostData, null, 2),
      );
      console.log("[ReviewModal] Payload ATS fields:", {
        appJobId: jobPostData.appJobId,
        appType: jobPostData.appType,
      });

      const orgNameValue = org?.orgName || orgName || "Unknown";

      // Use retry logic for API calls
      let publishedJobPostId: string | undefined;

      if (mode === "edit" && jobPostId) {
        // Update existing job post
        console.log("[ReviewModal] Updating job post with ID:", jobPostId);
        await withRetry(
          () =>
            jobDescriptionService.updateJobDescription(
              jobPostData as JobDescriptionEntity,
              jobPostId,
            ),
          3, // max retries
          1000, // initial delay
          (error) => {
            // Retry on network errors or 5xx errors
            const status = error?.response?.status;
            return !status || status >= 500;
          },
        );
        message.success("Job post updated successfully!");
        publishedJobPostId = jobPostId;
      } else {
        // Create new job post (new or clone)
        console.log("[ReviewModal] Creating new job post");
        const createdJobPost = await withRetry(
          () =>
            jobDescriptionService.addJobDescription(jobPostData, orgNameValue),
          3, // max retries
          1000, // initial delay
          (error) => {
            // Retry on network errors or 5xx errors
            const status = error?.response?.status;
            return !status || status >= 500;
          },
        );
        message.success("Job post created successfully!");
        publishedJobPostId = createdJobPost?._id;

        // Update measurement count after successful publish (new/clone only)
        changeSubscriptionMeasurements(1, "jobPosts");

        // If coming from onboarding flow, set the completed flag and clean up
        if (mode === "new" && sessionStorage.getItem("onboarding_redirect") === "true") {
          const user = AuthService.getInstance().getCurrentUser();
          if (user?._id) {
            localStorage.setItem(`onboarding_completed_${user._id}`, "true");
          }
          sessionStorage.removeItem("onboarding_redirect");
          sessionStorage.removeItem("onboarding_generated_job_post");
        }
      }

      // Auto-create simulation blueprints for any simulation rounds
      if (publishedJobPostId) {
        const step3Rounds = completeFormData.step3?.interviewRounds || [];
        const jdText = completeFormData.step1?.description || "";
        const roleTitle = completeFormData.step1?.position || "";

        for (const round of step3Rounds) {
          const metadata = (round as any)._displayMetadata;
          const answerType = metadata?.answerType;
          const roundId = round.interviewRoundId || metadata?._id;

          if (answerType === "simulation" && roundId) {
            // Fire-and-forget: don't block publish on blueprint creation
            simulationService
              .createBlueprint({
                jobId: publishedJobPostId,
                roundId,
                jdText,
                roleTitle,
              })
              .then((blueprint) => {
                if (blueprint) {
                  console.log(
                    "[ReviewModal] Simulation blueprint created for round:",
                    roundId,
                    blueprint,
                  );
                } else {
                  console.warn(
                    "[ReviewModal] Failed to create simulation blueprint for round:",
                    roundId,
                  );
                }
              });
          }
        }
      }

      // Clear draft ONLY after successful publish/update
      const { userId, orgId } = getCurrentUserAndOrg();
      const storageKey = generateStorageKey(mode, draftId, userId, orgId);
      localStorage.removeItem(storageKey);

      onClose();

      // Navigate to job post details page if we have the ID, otherwise fallback to job posts list
      if (publishedJobPostId) {
        navigate(`/job-posts/${publishedJobPostId}`);
      } else {
        navigate("/job-posts");
      }
    } catch (error) {
      // Log error with context
      logError("ReviewModal.handlePublishClick", error, {
        mode,
        jobPostId,
        step1Complete: validateStep1Complete(formData.step1),
        step2Complete: validateStep2Complete(formData.step2),
        step3Complete: validateStep3Complete(formData.step3),
        step5Complete: validateStep5Complete(formData.step5),
      });

      // Show user-friendly error message
      const errorMessage = getUserFriendlyErrorMessage(
        error,
        mode === "edit"
          ? "Failed to update job post. Your draft has been saved and you can try again."
          : "Failed to create job post. Your draft has been saved and you can try again.",
      );
      message.error(errorMessage, 5);

      // Don't clear draft on error - user can retry
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveDraftClick = () => {
    message.success("Draft saved successfully");
    onClose();
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const step3Rounds = formData.step3?.interviewRounds || [];
    const hasJobAlignment = formData.step4?.jobAlignment != null;
    const totalInterviewRounds = step3Rounds.length + (hasJobAlignment ? 1 : 0);

    return {
      totalRequirements:
        (formData.step2?.requirements || []).length +
        (formData.step2?.additionalRequirements || []).length,
      totalInterviewRounds,
      totalInterviewers: (formData.step5?.interviewers || []).length,
    };
  }, [formData]);
  const isRequirementsError = useMemo(() => {
    return !validateStep2Complete(formData.step2);
  }, [formData.step2]);

  const isInterviewRoundsError = useMemo(() => {
    return !validateStep3Complete(formData.step3);
  }, [formData.step3]);

  const isInterviewersError = useMemo(() => {
    return !validateStep5Complete(formData.step5);
  }, [formData.step5]);

  const handleModalClose = () => {
    onClose();
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={handleModalClose}
        maskClosable={false}
        width="100%"
        style={{ maxWidth: 1400 }}
        className="review-modal"
        footer={null}
        title={
          <div className="review-modal-header">
            <Title level={4} style={{ margin: 0 }}>
              Review & Publish
            </Title>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleModalClose}
              disabled={isPublishing}
            />
          </div>
        }
        closeIcon={null}
        centered
      >
        <Spin spinning={isPublishing} size="large" tip="Publishing job post...">
          {/* Review Modal Content */}
          <section className="review-modal-content">
            {/* Show validation warning if steps incomplete */}
            {!allStepsComplete && (
              <div>
                <Alert
                  message="Please complete all steps before publishing"
                  description={
                    incompleteSteps.length > 0
                      ? `The following steps need to be completed: ${incompleteSteps
                          .map((s) => "Step " + s)
                          .join(", ")}`
                      : "Some steps are incomplete. Please review and complete them."
                  }
                  type="warning"
                  showIcon
                />
              </div>
            )}

            {/* Summary Statistics */}
            <div className="review-modal-summary-cards">
              <div className="review-modal-summary-card requirements-card">
                <div className="review-modal-summary-card-header">
                  <Text style={{ fontSize: "14px", fontWeight: "500" }}>
                    Requirements
                  </Text>

                  {isRequirementsError ? (
                    <CloseCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.error,
                      }}
                    />
                  ) : (
                    <CheckCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.hired,
                      }}
                    />
                  )}
                </div>

                <div className="review-modal-summary-card-content">
                  <Text style={{ fontSize: "14px" }}>
                    Must Match Requirements: &nbsp;
                    <Text strong style={{ fontSize: "14px" }}>
                      {formData.step2?.requirements?.filter(
                        (requirement) => requirement.mustMatch,
                      ).length || 0}
                    </Text>
                  </Text>
                  <Text style={{ fontSize: "14px" }}>
                    Other Requirements: &nbsp;
                    <Text strong style={{ fontSize: "14px" }}>
                      {formData.step2?.requirements?.filter(
                        (requirement) => !requirement.mustMatch,
                      ).length || 0}
                    </Text>
                  </Text>
                </div>
              </div>

              <div className="review-modal-summary-card interview-rounds-card">
                <div className="review-modal-summary-card-header">
                  <Text style={{ fontSize: "14px", fontWeight: "500" }}>
                    Assessments
                  </Text>
                  {isInterviewRoundsError ? (
                    <CloseCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.error,
                      }}
                    />
                  ) : (
                    <CheckCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.hired,
                      }}
                    />
                  )}
                </div>
                <div className="review-modal-summary-card-content">
                  <Text style={{ fontSize: "14px" }}>
                    Total Assessments: &nbsp;
                    <Text strong style={{ fontSize: "14px" }}>
                      {summaryStats.totalInterviewRounds}
                    </Text>
                  </Text>
                </div>
              </div>

              <div className="review-modal-summary-card interviewers-card">
                <div className="review-modal-summary-card-header">
                  <Text style={{ fontSize: "14px", fontWeight: "500" }}>
                    Post Assessment
                  </Text>
                  {isInterviewersError ? (
                    <CloseCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.error,
                      }}
                    />
                  ) : (
                    <CheckCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.hired,
                      }}
                    />
                  )}
                </div>
                <div className="review-modal-summary-card-content">
                  <Text style={{ fontSize: "14px" }}>
                    Mode of Interview: &nbsp;
                    <Text strong style={{ fontSize: "14px" }}>
                      {formData.step5?.onlineInterview
                        ? "Online"
                        : "Team Will Contact"}
                    </Text>
                  </Text>
                  {formData.step5?.onlineInterview && (
                    <Text style={{ fontSize: "14px" }}>
                      Total Interviewers: &nbsp;
                      <Text strong style={{ fontSize: "14px" }}>
                        {formData.step5?.interviewers?.length}
                      </Text>
                    </Text>
                  )}
                </div>
              </div>

              <div className="review-modal-summary-card status-card">
                <div className="review-modal-summary-card-header">
                  <Text style={{ fontSize: "14px", fontWeight: "500" }}>
                    Status
                  </Text>
                  {allStepsComplete ? (
                    <CheckCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.hired,
                      }}
                    />
                  ) : (
                    <CloseCircleOutlined
                      style={{
                        fontSize: "20px",
                        color: CHART_COLORS.error,
                      }}
                    />
                  )}
                </div>
                <div className="review-modal-summary-card-content">
                  <Text
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: allStepsComplete
                        ? CHART_COLORS.hired
                        : CHART_COLORS.error,
                    }}
                  >
                    {allStepsComplete
                      ? "Ready to Publish"
                      : "Some steps are incomplete. Please review and complete them."}
                  </Text>
                </div>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="review-modal-body">
              <section className="form-summary-cards-grid">
                <ReviewSummaryCard
                  title="Basic Details"
                  stepNumber={1}
                  isComplete={isStepComplete(1)}
                  onEdit={() => handleEditStep(1)}
                  content={
                    <ReviewSummaryContents formData={formData} stepNumber={1} />
                  }
                />

                <ReviewSummaryCard
                  title="Requirements"
                  stepNumber={2}
                  isComplete={isStepComplete(2)}
                  onEdit={() => handleEditStep(2)}
                  content={
                    <ReviewSummaryContents formData={formData} stepNumber={2} />
                  }
                />

                <ReviewSummaryCard
                  title="Assessments"
                  stepNumber={3}
                  isComplete={isStepComplete(3)}
                  onEdit={() => handleEditStep(3)}
                  content={
                    <ReviewSummaryContents formData={formData} stepNumber={3} />
                  }
                />

                <ReviewSummaryCard
                  title="Job Alignment"
                  stepNumber={4}
                  isComplete={isStepComplete(4)}
                  onEdit={() => handleEditStep(4)}
                  content={
                    <ReviewSummaryContents formData={formData} stepNumber={4} />
                  }
                />

                <ReviewSummaryCard
                  title="Post Assessment"
                  stepNumber={5}
                  isComplete={isStepComplete(5)}
                  onEdit={() => handleEditStep(5)}
                  content={
                    <ReviewSummaryContents formData={formData} stepNumber={5} />
                  }
                />
              </section>
            </div>

            <Divider style={{ margin: 0 }} />

            <div className="review-modal-footer">
              <Button onClick={handleModalClose}>Close</Button>
              <div className="review-modal-footer-buttons">
                {mode !== "edit" && (
                  <Button
                    onClick={handleSaveDraftClick}
                    disabled={isPublishing}
                  >
                    Save as Draft
                  </Button>
                )}
                <Button
                  type="primary"
                  onClick={handlePublishClick}
                  disabled={!allStepsComplete || isPublishing}
                  loading={isPublishing}
                >
                  {mode === "edit" ? "Update" : "Publish"}
                </Button>
              </div>
            </div>
          </section>
        </Spin>
      </Modal>

      {/* Upgrade Popup */}
      <SubscriptionWarning
        open={showUpgradePopup}
        handleClose={() => setShowUpgradePopup(false)}
        title="Limit reached"
        type="job-post-limit"
      />
    </>
  );
};

export default ReviewModal;
